import { observable, action, computed } from "mobx";
import { TypeaheadSelection } from "uitk-react-typeahead";
import { TypeaheadDataProps, TypeaheadSelectionProps } from "src/components/flexComponents/WizardHotelPWA/typings";
import { LocationState } from "../global/location/typings";
import { GtConfig } from "../../config/staticConfigs/car/typings";
import { CarLastSearchData } from "../../LastSearchData";
import { LocationType } from "../typings";
import { RouteStore } from "bernie-core";

export class GroundTransportationLocationState {
  @computed
  public get destinationConfig() {
    return this.config.location.destination;
  }

  @computed
  public get originConfig() {
    return this.config.location.origin;
  }

  @computed
  public get lastSearch() {
    return this.lastSearchData;
  }

  @observable
  public config: GtConfig;

  @observable
  public location: LocationState;

  @observable
  public isOriginAirport = true;

  @observable
  public destinationInvalidKey: string = "";

  @observable
  public originInvalidKey: string | "";

  @observable
  public lastSearchData: CarLastSearchData;

  @observable
  public regionId: string = "";

  @observable
  public hotelId: string = "";

  @observable
  public airportCode: string = "";

  @action
  public resetValidations = () => {
    this.originInvalidKey = "";
    this.destinationInvalidKey = "";
  };

  @action
  public swapTypeaheadLocations() {
    this.destinationInvalidKey = "";
    this.originInvalidKey = "";
    this.swapTypeaheadConfigs();
    this.swapTypeaheadCurrentData();
    this.isOriginAirport = !this.isOriginAirport;
    this.lastSearchData.groundTransport.isOriginAirport = this.isOriginAirport;
  }

  @action
  public swapTypeaheadConfigs() {
    const tempDestinationConfig = this.config.location.destination;
    this.config.location.destination = this.config.location.origin;
    this.config.location.origin = tempDestinationConfig;
  }

  @action
  public swapTypeaheadCurrentData() {
    const tempDestinationLocation = this.location.destination;
    this.location.destination = this.location.origin;
    this.location.origin = tempDestinationLocation;
  }

  @action
  public updateDestinationSelection(selection: TypeaheadSelection) {
    const { term, data } = selection as TypeaheadSelectionProps;
    this.location.destination.value = term;
    this.location.destination.metaData.regionId = data?.regionId || data?.cityId;
    this.location.destination.metaData.ttla = data?.hierarchyInfo?.airport?.airportCode;
    this.location.destination.metaData.countryCode = data?.hierarchyInfo?.country?.isoCode2;
    this.location.destination.metaData.shortName = data?.regionNames?.shortName;
    this.location.destination.metaData.latLong =
      data?.location?.lat && data?.location?.long ? `${data.location.lat},${data.location.long}` : "";
    this.handleLocationData(data);

    this.destinationInvalidKey = "";
  }

  @action
  public updateOriginSelection(selection: TypeaheadSelection) {
    const { term, data } = selection as TypeaheadSelectionProps;
    this.location.origin.value = term;
    this.location.origin.metaData.regionId = data && data.regionId;
    this.location.origin.metaData.ttla = data?.hierarchyInfo?.airport.airportCode;
    this.location.origin.metaData.countryCode = data?.hierarchyInfo?.country?.isoCode2;
    this.location.origin.metaData.shortName = data?.regionNames?.shortName;
    this.location.origin.metaData.latLong =
      data?.location?.lat && data?.location?.long ? `${data.location.lat},${data.location.long}` : "";
    this.handleLocationData(data);

    this.originInvalidKey = "";
  }

  public updateFieldsFromQueryParams = (routeStore: RouteStore) => {
    const { location, hotelName } = routeStore.queryParams;
    const locationFromTypeKey = this.isOriginAirport ? "origin" : "destination";
    const locationToTypeKey = this.isOriginAirport ? "destination" : "origin";

    if (location) {
      this.location[locationFromTypeKey].value = decodeURI(location);
    }
    if (hotelName) {
      this.location[locationToTypeKey].value = decodeURI(hotelName);
    }
  };

  public validateOriginField = (): boolean => !!this.location.origin.value;

  public validateDestinationField = (): boolean => !!this.location.destination.value;

  public updateStateFromCookie = () => {
    const lastSearch = this.lastSearchData?.groundTransport;
    const originStatus = this.lastSearchData?.groundTransport.isOriginAirport;
    const currentOriginValue = this.location.origin.value;
    const currentDestinationValue = this.location.destination.value;

    if (lastSearch) {
      const originCookieValue = decodeURIComponent(lastSearch.location.origin?.value);
      const destinationCookieValue = decodeURIComponent(lastSearch.location.destination?.value);
      const needsOriginUpdate = originCookieValue === currentOriginValue || currentOriginValue === "";
      const needsDestinationUpdate =
        destinationCookieValue === currentDestinationValue || currentDestinationValue === "";

      if (needsOriginUpdate) {
        this.updateOriginFromCookie(
          originCookieValue ?? "",
          lastSearch.location.origin?.regionID ?? "",
          lastSearch.location.origin?.countryCode ?? "",
          lastSearch.location.origin?.latLong ?? "",
          lastSearch.location.origin?.ttla ?? "",
          this.lastSearchData?.timestamp
        );
        if (this.isOriginAirport) {
          this.airportCode = lastSearch.location.origin?.ttla ?? "";
        } else {
          this.hotelId = lastSearch.location.origin?.hotelID ?? "";
        }
      }

      if (needsDestinationUpdate) {
        this.updateDestinationFromCookie(
          destinationCookieValue ?? "",
          lastSearch.location.destination?.regionID ?? "",
          lastSearch.location.destination?.countryCode ?? "",
          lastSearch.location.destination?.latLong ?? "",
          lastSearch.location.destination?.ttla ?? "",
          this.lastSearchData?.timestamp
        );
        if (!this.isOriginAirport) {
          this.airportCode = lastSearch.location.destination?.ttla ?? "";
        } else {
          this.hotelId = lastSearch.location.destination?.hotelID ?? "";
        }
      }
      if (this.isOriginAirport !== originStatus) {
        this.swapTypeaheadConfigs();
        this.isOriginAirport = originStatus ?? true;
      }
    }
  };

  public updateDestinationFromCookie(
    value: string,
    regionID: string,
    countryCode: string,
    latLong: string,
    ttla: string,
    timestamp: number
  ) {
    this.location.destination.value = value;
    this.location.destination.metaData.regionId = regionID;
    this.location.destination.metaData.ttla = ttla;
    this.location.destination.metaData.countryCode = countryCode;
    this.location.destination.metaData.latLong = latLong;
    this.location.destination.metaData.lastSearchTimestamp = timestamp;
    this.location.destination.metaData.lastSearchLatLong = latLong;
  }

  public updateOriginFromCookie(
    value: string,
    regionID: string,
    countryCode: string,
    latLong: string,
    ttla: string,
    timestamp: number
  ) {
    this.location.origin.value = value;
    this.location.origin.metaData.regionId = regionID;
    this.location.origin.metaData.ttla = ttla;
    this.location.origin.metaData.countryCode = countryCode;
    this.location.origin.metaData.latLong = latLong;
    this.location.origin.metaData.lastSearchTimestamp = timestamp;
    this.location.origin.metaData.lastSearchLatLong = latLong;
  }

  private handleLocationData = (data: TypeaheadDataProps) => {
    if (data.type === LocationType.HOTEL) {
      this.hotelId = data?.selected;
      this.location.origin.metaData.hotelId = data?.selected;
      this.location.destination.metaData.hotelId = data?.selected;
    } else {
      this.regionId = data?.hierarchyInfo.airport.airportId;
      this.airportCode = data?.hierarchyInfo?.airport?.airportCode;
    }
  };

  constructor(config: GtConfig, location: LocationState, lastSearchData?: CarLastSearchData) {
    this.config = config;
    this.location = location;
    if (lastSearchData) {
      this.lastSearchData = lastSearchData;
    }
  }
}
